import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { flatten } from 'flat';
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _set from 'lodash/set';
import { decodeQueryParams, QueryParamConfigMap, searchStringToObject, useQueryParams } from 'use-query-params';

import { isEmpty } from '@/utils/formChangeCounter';

export const useQueryPersistence = <FormValues extends object>(
  defaultValues: FormValues,
  currentValues: FormValues | null,
  configMap: QueryParamConfigMap
) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [, setUrlQueryParams] = useQueryParams(configMap);

  const defaultQueryValues = useMemo(() => {
    const queryStateDraft = _cloneDeep(defaultValues);
    const entries = Object.entries(decodeQueryParams(configMap, searchStringToObject(location.search)));
    entries.forEach(([key, value]) => _set(queryStateDraft, key, value));

    return queryStateDraft;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentValues) return;

    const filter = flatten<object, object>(currentValues, { safe: true });

    const filterEntries = Object.entries(filter).filter(
      ([key, value]) => !isEmpty(value) && !_isEqual(_get(defaultValues, key), value)
    );

    setUrlQueryParams(Object.fromEntries(filterEntries), 'replace');
  }, [defaultValues, currentValues, navigate, setUrlQueryParams]);

  return defaultQueryValues;
};
